import React, { useCallback, useEffect, useState } from "react";
import DataSetupMainCard from "./DataSetupMainCard";
import DataSetupDrawer from "./DataSetupDrawer";
import { MuiTable, TextValidator, Label, Autocomplete } from "components";
import {
  IconButton,
  Tooltip,
  Box,
  Grid,
  useTheme,
  Chip,
  Dialog,
  Typography,
  CircularProgress,
  Switch,
  Grow,
  Stack,
  FormControlLabel,
  Checkbox,
  Button,
  alpha,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import StatusConfirmDialog from "components/StatusConfirmDialog";
import MenuItemService from "services/MenuItemService";
import CategoryService from "services/CategoryService";
import SubCategoryService from "services/SubCategoryService";
import ModifierService from "services/ModifierService";
import FileUpload from "components/FileUpload";
import ImageLoader from "components/ImageLoader";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

const MenuItemsSetupCard = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [data, setData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [modifiers, setModifiers] = useState([]);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    search: null,
  });

  const [drawer, setDrawer] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    basePrice: "",
    preparationTime: "",
    displayOrder: "",
    categoryId: null,
    subcategoryId: null,
    hasVariations: false,
    isVegetarian: false,
    isVegan: false,
    isSpicy: false,
    image: [],
    currentImage: "",
    itemVariations: [
      { name: "Small", price: "", displayOrder: 0, isActive: true },
      { name: "Medium", price: "", displayOrder: 1, isActive: true },
      { name: "Large", price: "", displayOrder: 2, isActive: true },
    ],
    modifierOptions: [],
  });

  // Status update state
  const [statusDialog, setStatusDialog] = useState({
    open: false,
    loading: false,
    menuItemId: null,
    menuItemName: "",
    newStatus: null,
  });

  // View modal state
  const [viewModal, setViewModal] = useState({
    open: false,
    item: null,
  });

  const setPage = (page) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: page,
    }));
  };

  const setPageSize = (pageSize) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: 1,
      limit: pageSize,
    }));
  };

  const fetchModifiers = useCallback(async () => {
    try {
      const response = await ModifierService.getAll({
        page: 0,
        limit: 100,
        isActive: true,
      });
      if (response.success) {
        setModifiers(response?.data?.view?.data || []);
      }
    } catch (error) {
      console.error("Error fetching modifiers:", error);
    }
  }, []);

  const getData = useCallback(async () => {
    setLoading(true);
    const response = await MenuItemService.getAll({
      ...filters,
      page: filters.page - 1,
    });
    if (response.success) {
      setData(response?.data?.view?.data || []);
      setTotalItems(response?.data?.view?.totalItems || 0);
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    getData();
    fetchCategories();
    fetchModifiers();
  }, [getData, fetchModifiers]);

  const fetchCategories = async () => {
    try {
      const response = await CategoryService.getAll({
        page: 0,
        limit: 100,
        isActive: true,
      });
      if (response.success) {
        setCategories(response?.data?.view?.data || []);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchSubCategories = async (categoryId) => {
    try {
      const response = await SubCategoryService.getAll({
        page: 0,
        limit: 100,
        categoryId: categoryId,
        isActive: true,
      });
      if (response.success) {
        setSubCategories(response?.data?.view?.data || []);
      }
    } catch (error) {
      console.error("Error fetching sub categories:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImageChange = (files) => {
    setFormData((prev) => ({
      ...prev,
      image: files,
    }));
  };

  const handleVariationPriceChange = (index, price) => {
    setFormData((prev) => {
      const newVariations = [...prev.itemVariations];
      newVariations[index] = {
        ...newVariations[index],
        price: price,
      };
      return {
        ...prev,
        itemVariations: newVariations,
      };
    });
  };

  const toggleVariationActive = (index) => {
    setFormData((prev) => {
      const newVariations = [...prev.itemVariations];
      newVariations[index] = {
        ...newVariations[index],
        isActive: !newVariations[index].isActive,
      };
      return {
        ...prev,
        itemVariations: newVariations,
      };
    });
  };

  const addModifierOption = () => {
    setFormData((prev) => ({
      ...prev,
      modifierOptions: [
        ...prev.modifierOptions,
        {
          modifierId: null,
          name: "",
          price: "",
          displayOrder: prev.modifierOptions.length,
          isActive: true,
        },
      ],
    }));
  };

  const removeModifierOption = (index) => {
    setFormData((prev) => {
      const newModifiers = prev.modifierOptions.filter((_, i) => i !== index);
      // Recalculate display orders
      return {
        ...prev,
        modifierOptions: newModifiers.map((mod, idx) => ({
          ...mod,
          displayOrder: idx,
        })),
      };
    });
  };

  const handleModifierOptionChange = (index, field, value) => {
    setFormData((prev) => {
      const newModifiers = [...prev.modifierOptions];
      newModifiers[index] = {
        ...newModifiers[index],
        [field]: value,
      };
      // If modifier is selected, auto-fill the name
      if (field === "modifierId" && value) {
        const selectedModifier = modifiers.find((m) => m.id === value.id);
        if (selectedModifier) {
          newModifiers[index].name = selectedModifier.name;
        }
      }
      return {
        ...prev,
        modifierOptions: newModifiers,
      };
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const formPayload = new FormData();
      formPayload.append("name", formData.name);
      formPayload.append("description", formData.description);
      formPayload.append("basePrice", formData.basePrice);
      formPayload.append("preparationTime", formData.preparationTime);
      formPayload.append("displayOrder", formData.displayOrder);
      formPayload.append(
        "categoryId",
        formData.categoryId?.id || formData.categoryId,
      );
      formPayload.append(
        "subcategoryId",
        formData.subcategoryId?.id || formData.subcategoryId,
      );
      formPayload.append("hasVariations", formData.hasVariations);
      formPayload.append("isVegetarian", formData.isVegetarian);
      formPayload.append("isVegan", formData.isVegan);
      formPayload.append("isSpicy", formData.isSpicy);
      formPayload.append("companyId", filters.companyId);

      // Add item variations (only active ones with price)
      const activeVariations = formData.itemVariations
        .filter((v) => v.isActive && v.price && parseFloat(v.price) > 0)
        .map((v, index) => ({
          name: v.name,
          price: parseFloat(v.price),
          displayOrder: index,
          isActive: true,
        }));
      if (activeVariations.length > 0) {
        formPayload.append("itemVariations", JSON.stringify(activeVariations));
      }

      // Add modifier options
      const validModifierOptions = formData.modifierOptions
        .filter(
          (m) => m.modifierId && m.name && m.price && parseFloat(m.price) >= 0,
        )
        .map((m) => ({
          modifierId: m.modifierId?.id || m.modifierId,
          name: m.name,
          price: parseFloat(m.price),
          displayOrder: m.displayOrder,
          isActive: m.isActive,
        }));
      if (validModifierOptions.length > 0) {
        formPayload.append(
          "modifierOptions",
          JSON.stringify(validModifierOptions),
        );
      }

      if (formData.image.length > 0) {
        formPayload.append("image", formData.image[0]);
      }

      if (isEdit) {
        await MenuItemService.update(editingId, formPayload);
      } else {
        await MenuItemService.create(formPayload);
      }

      setFormData({
        name: "",
        description: "",
        basePrice: "",
        preparationTime: "",
        displayOrder: "",
        categoryId: null,
        subcategoryId: null,
        hasVariations: false,
        isVegetarian: false,
        isVegan: false,
        isSpicy: false,
        image: [],
        currentImage: "",
        itemVariations: [
          { name: "Small", price: "", displayOrder: 0, isActive: true },
          { name: "Medium", price: "", displayOrder: 1, isActive: true },
          { name: "Large", price: "", displayOrder: 2, isActive: true },
        ],
        modifierOptions: [],
      });
      setEditingId(null);
      setDrawer(false);
      getData();
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleEditClick = async (item) => {
    setEditingId(item.id);
    let fetchedSubCategories = [];

    // Fetch subcategories for the selected category
    if (item.categoryId) {
      try {
        const response = await SubCategoryService.getAll({
          page: 0,
          limit: 100,
          categoryId: item.categoryId,
          isActive: true,
        });
        if (response.success) {
          fetchedSubCategories = response?.data?.view?.data || [];
          setSubCategories(fetchedSubCategories);
        }
      } catch (error) {
        console.error("Error fetching sub categories:", error);
      }
    }

    setFormData({
      name: item.name || "",
      description: item.description || "",
      basePrice: item.basePrice || "",
      preparationTime: item.preparationTime || "",
      displayOrder: item.displayOrder || "",
      categoryId: categories.find((cat) => cat.id === item.categoryId) || null,
      subcategoryId:
        fetchedSubCategories.find((sub) => sub.id === item.subcategoryId) ||
        null,
      hasVariations: item.hasVariations || false,
      isVegetarian: item.isVegetarian || false,
      isVegan: item.isVegan || false,
      isSpicy: item.isSpicy || false,
      image: [],
      currentImage: item.image || "",
    });
    setIsEdit(true);
    setDrawer(true);
  };

  const handleAddClick = () => {
    setEditingId(null);
    setFormData({
      name: "",
      description: "",
      basePrice: "",
      preparationTime: "",
      displayOrder: "",
      categoryId: null,
      subcategoryId: null,
      hasVariations: false,
      isVegetarian: false,
      isVegan: false,
      isSpicy: false,
      image: [],
      currentImage: "",
    });
    setSubCategories([]);
    setIsEdit(false);
    setDrawer(true);
  };

  /**
   * Handle view button click
   */
  const handleViewClick = (item) => {
    setViewModal({
      open: true,
      item: item,
    });
  };

  /**
   * Handle status toggle button click
   */
  const handleStatusToggle = (item) => {
    const newStatus = !item.isActive;
    setStatusDialog({
      open: true,
      loading: false,
      menuItemId: item.id,
      menuItemName: item.name,
      newStatus: newStatus,
    });
  };

  /**
   * Confirm status change and call API
   */
  const handleStatusConfirm = async () => {
    const { menuItemId, newStatus } = statusDialog;

    try {
      setStatusDialog((prev) => ({ ...prev, loading: true }));

      const payload = {
        isActive: newStatus,
        companyId: filters.companyId,
      };

      const response = await MenuItemService.updateStatus(menuItemId, payload);

      if (response.success) {
        await getData();
        console.log("✅ Menu Item status updated successfully");
      } else {
        console.error("❌ Failed to update menu item status:", response.error);
      }

      setStatusDialog((prev) => ({ ...prev, open: false, loading: false }));
    } catch (error) {
      console.error("Error updating status:", error);
      setStatusDialog((prev) => ({ ...prev, open: false, loading: false }));
    }
  };

  return (
    <DataSetupMainCard
      iconSrc="/assets/images/icons/menu.png"
      title="Menu Items"
      subtitle="Manage your menu items"
      actionIconSrc="/assets/images/icons/plus.png"
      actionText="Add Menu Item"
      onAddClick={handleAddClick}
    >
      <MuiTable
        columns={[
          {
            accessorKey: "actions",
            header: "Actions",
            Cell: ({ cell }) => {
              const item = cell.row.original;
              const isUpdating =
                statusDialog.menuItemId === item.id && statusDialog.loading;

              return (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Tooltip title="View">
                    <IconButton
                      size="small"
                      color="info"
                      onClick={() => handleViewClick(item)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditClick(item)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={item.isActive ? "Deactivate" : "Activate"}>
                    <Box sx={{ position: "relative" }}>
                      <Switch
                        checked={item.isActive}
                        size="small"
                        disabled={isUpdating}
                        onChange={() => handleStatusToggle(item)}
                      />
                      {isUpdating && (
                        <CircularProgress
                          size={20}
                          sx={{
                            position: "absolute",
                            left: "50%",
                            top: "50%",
                            transform: "translate(-50%, -50%)",
                            opacity: 0.7,
                          }}
                        />
                      )}
                    </Box>
                  </Tooltip>
                </Box>
              );
            },
          },
          {
            accessorKey: "name",
            header: "Name",
          },
          {
            accessorKey: "Category.name",
            header: "Category",
          },
          {
            accessorKey: "SubCategory.name",
            header: "Sub Category",
          },
          {
            accessorKey: "basePrice",
            header: "Price",
          },
          {
            accessorKey: "description",
            header: "Description",
          },
          {
            accessorKey: "image",
            header: "Image",
            Cell: ({ cell }) => (
              <ImageLoader
                imagePath={cell.getValue()}
                width={80}
                height={80}
                alt={cell.row.original.name}
                rounded={true}
                borderRadius={1}
              />
            ),
          },
          {
            accessorKey: "isActive",
            header: "Status",
            Cell: ({ cell }) => {
              const isActive = cell.getValue();

              return (
                <Chip
                  label={isActive ? "Active" : "Inactive"}
                  variant="filled"
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    letterSpacing: 0.3,
                    background: isActive
                      ? `linear-gradient(135deg, ${theme.palette.success.main}15 0%, ${theme.palette.success.light}15 100%)`
                      : `linear-gradient(135deg, ${theme.palette.warning.main}15 0%, ${theme.palette.warning.light}15 100%)`,
                    color: isActive
                      ? theme.palette.success.dark
                      : theme.palette.warning.dark,
                    border: `2px solid ${
                      isActive
                        ? theme.palette.success.main
                        : theme.palette.warning.main
                    }`,
                    borderRadius: "20px",
                    padding: "4px 8px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: isActive
                        ? `0 4px 12px ${theme.palette.success.main}40`
                        : `0 4px 12px ${theme.palette.warning.main}40`,
                    },
                  }}
                />
              );
            },
          },
        ]}
        data={data}
        title=""
        manualPagination={true}
        rowCount={totalItems}
        currentPage={filters.page}
        pageSize={filters.limit}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        loading={loading}
      />

      <DataSetupDrawer
        maxWidth="md"
        open={drawer}
        onClose={() => setDrawer(false)}
        iconSrc="/assets/images/icons/menu.png"
        title={isEdit ? "Edit Menu Item" : "Add Menu Item"}
        subtitle="Create and manage menu item details"
        submitLabel={isEdit ? "Update" : "Create"}
        onSubmit={handleFormSubmit}
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box gap={1} display="flex" flexDirection="column">
              {/* Menu Item Name */}
              <TextValidator
                label="Menu Item Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                validators={["required", "minStringLength:2"]}
                errorMessages={[
                  "Menu item name is required",
                  "Menu item name must be at least 2 characters",
                ]}
                fullWidth
                size="small"
                placeholder="Enter menu item name"
              />

              {/* Category */}
              <Autocomplete
                name="categoryId"
                label="Category"
                value={formData.categoryId}
                onChange={(event, newValue) => {
                  setFormData((prev) => ({
                    ...prev,
                    categoryId: newValue,
                    subcategoryId: null,
                  }));
                  if (newValue?.id) {
                    fetchSubCategories(newValue.id);
                  }
                }}
                options={categories}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                validators={["required"]}
                errorMessages={["Category is required"]}
                fullWidth
                size="small"
              />

              {/* Sub Category */}
              <Autocomplete
                name="subcategoryId"
                label="Sub Category"
                value={formData.subcategoryId}
                onChange={(event, newValue) => {
                  setFormData((prev) => ({
                    ...prev,
                    subcategoryId: newValue,
                  }));
                }}
                options={subCategories}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                validators={["required"]}
                errorMessages={["Sub Category is required"]}
                fullWidth
                size="small"
              />

              {/* Base Price */}
              <TextValidator
                label="Base Price"
                name="basePrice"
                value={formData.basePrice}
                onChange={handleInputChange}
                validators={["required", "isNumber"]}
                errorMessages={[
                  "Base price is required",
                  "Base price must be a number",
                ]}
                fullWidth
                size="small"
                type="number"
                placeholder="Enter base price"
                inputProps={{ step: "0.01" }}
              />
              {/* Display Order */}
              <TextValidator
                label="Display Order"
                name="displayOrder"
                value={formData.displayOrder}
                onChange={handleInputChange}
                validators={["required", "isNumber"]}
                errorMessages={[
                  "Display order is required",
                  "Display order must be a number",
                ]}
                fullWidth
                size="small"
                type="number"
                placeholder="Enter display order"
              />
              {/* Description */}
              <TextValidator
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                validators={["required"]}
                errorMessages={["Description is required"]}
                fullWidth
                size="small"
                multiline
                rows={3}
                placeholder="Describe the menu item"
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {/* Image Upload */}
            <Box gap={1} display="flex" flexDirection="column">
              {/* Preparation Time */}
              <TextValidator
                label="Preparation Time (minutes)"
                name="preparationTime"
                value={formData.preparationTime}
                onChange={handleInputChange}
                validators={["required", "isNumber"]}
                errorMessages={[
                  "Preparation time is required",
                  "Preparation time must be a number",
                ]}
                fullWidth
                size="small"
                type="number"
                placeholder="Enter preparation time"
              />

              {/* Dietary Options */}
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isVegetarian"
                      checked={formData.isVegetarian}
                      onChange={handleInputChange}
                    />
                  }
                  label="Vegetarian"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isVegan"
                      checked={formData.isVegan}
                      onChange={handleInputChange}
                    />
                  }
                  label="Vegan"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isSpicy"
                      checked={formData.isSpicy}
                      onChange={handleInputChange}
                    />
                  }
                  label="Spicy"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="hasVariations"
                      checked={formData.hasVariations}
                      onChange={handleInputChange}
                    />
                  }
                  label="Has Variations"
                />
              </Box>
              <FileUpload
                value={formData.image}
                onChange={handleImageChange}
                multiple={false}
                required={false}
                acceptedFileTypes={["image/jpeg", "image/png", "image/webp"]}
                maxSingleFileSize={5 * 1024 * 1024}
                label={
                  formData.image.length > 0
                    ? "Change Image"
                    : "Upload Menu Item Image"
                }
                helperText="Accepted formats: JPG, PNG, WEBP (Max 5MB)"
              />
            </Box>
          </Grid>
          <Grid size={12}>
            {isEdit && formData.currentImage && (
              <Box
                sx={{
                  mb: 2,
                  borderRadius: 2,
                }}
              >
                <Label>Current Image Preview</Label>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "start",
                    borderRadius: 1,
                    overflow: "hidden",
                    mt: 1,
                  }}
                >
                  <ImageLoader
                    imagePath={formData.currentImage}
                    width={80}
                    height={80}
                    alt="Current menu item image"
                    rounded={true}
                    borderRadius={1}
                  />
                </Box>
              </Box>
            )}
          </Grid>

          {/* Item Variations Section */}
          <Grid size={12}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                }}
              >
                Item Variations
              </Typography>
              <Grid container spacing={2}>
                {formData.itemVariations.map((variation, index) => (
                  <Grid size={{ xs: 12, sm: 4 }} key={index}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: variation.isActive
                          ? "white"
                          : alpha("#000000", 0.03),
                        border: `2px solid ${
                          variation.isActive
                            ? theme.palette.primary.main
                            : alpha("#000000", 0.1)
                        }`,
                        transition: "all 0.3s ease",
                        cursor: "pointer",
                        "&:hover": {
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                        },
                      }}
                      onClick={() => toggleVariationActive(index)}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: variation.isActive
                              ? theme.palette.primary.main
                              : "#9ca3af",
                          }}
                        >
                          {variation.name}
                        </Typography>
                        <Checkbox
                          checked={variation.isActive}
                          size="small"
                          sx={{ p: 0 }}
                        />
                      </Box>
                      {variation.isActive && (
                        <TextValidator
                          label="Price (Rs.)"
                          value={variation.price}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleVariationPriceChange(index, e.target.value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          validators={["required", "isNumber"]}
                          errorMessages={[
                            "Price is required",
                            "Price must be a number",
                          ]}
                          fullWidth
                          size="small"
                          type="number"
                          placeholder="0.00"
                          inputProps={{ step: "0.01" }}
                        />
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>

          {/* Modifier Options Section */}
          <Grid size={12}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.secondary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.secondary.main,
                  }}
                >
                  Modifier Options
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addModifierOption}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    bgcolor: theme.palette.secondary.main,
                    "&:hover": {
                      bgcolor: theme.palette.secondary.dark,
                    },
                  }}
                >
                  Add Addons
                </Button>
              </Box>

              {formData.modifierOptions.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: "center",
                    color: "#9ca3af",
                    py: 2,
                  }}
                >
                  No addons options added yet. Click "Add Addons" to start.
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {formData.modifierOptions.map((modifier, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: "white",
                        border: `1px solid ${alpha("#000000", 0.1)}`,
                      }}
                    >
                      <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Autocomplete
                            name={`modifier-${index}`}
                            label="Addon"
                            value={modifier.modifierId}
                            onChange={(event, newValue) => {
                              handleModifierOptionChange(
                                index,
                                "modifierId",
                                newValue,
                              );
                            }}
                            options={modifiers}
                            getOptionLabel={(option) => option?.name || ""}
                            isOptionEqualToValue={(option, value) =>
                              option?.id === value?.id
                            }
                            validators={["required"]}
                            errorMessages={["Modifier is required"]}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextValidator
                            label="Option Name"
                            value={modifier.name}
                            onChange={(e) =>
                              handleModifierOptionChange(
                                index,
                                "name",
                                e.target.value,
                              )
                            }
                            validators={["required"]}
                            errorMessages={["Option name is required"]}
                            fullWidth
                            size="small"
                            placeholder="e.g., Extra Cheese"
                          />
                        </Grid>
                        <Grid size={{ xs: 10, sm: 3 }}>
                          <TextValidator
                            label="Price (Rs.)"
                            value={modifier.price}
                            onChange={(e) =>
                              handleModifierOptionChange(
                                index,
                                "price",
                                e.target.value,
                              )
                            }
                            validators={["required", "isNumber"]}
                            errorMessages={[
                              "Price is required",
                              "Price must be a number",
                            ]}
                            fullWidth
                            size="small"
                            type="number"
                            placeholder="0.00"
                            inputProps={{ step: "0.01" }}
                          />
                        </Grid>
                        <Grid size={{ xs: 2, sm: 1 }}>
                          <IconButton
                            color="error"
                            onClick={() => removeModifierOption(index)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Grid>
        </Grid>
        <></>
      </DataSetupDrawer>

      {/* Status Confirmation Dialog */}
      <StatusConfirmDialog
        open={statusDialog.open}
        onClose={() => setStatusDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={handleStatusConfirm}
        title={
          statusDialog.newStatus ? "Activate Menu Item" : "Deactivate Menu Item"
        }
        message={
          statusDialog.newStatus
            ? "Are you sure you want to activate this menu item? It will be visible to customers."
            : "Are you sure you want to deactivate this menu item? It will no longer be visible to customers."
        }
        itemName={statusDialog.menuItemName}
        newStatus={statusDialog.newStatus}
        loading={statusDialog.loading}
      />

      {/* View Menu Item Modal */}
      <Dialog
        open={viewModal.open}
        onClose={() => setViewModal({ open: false, item: null })}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Grow}
        transitionDuration={{
          enter: 400,
          exit: 300,
        }}
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            maxHeight: "90vh",
            boxShadow: `0 20px 60px rgba(0, 0, 0, 0.15)`,
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
            transition: "all 0.4s ease-in-out",
          },
        }}
      >
        {/* ===== Header with Icon ===== */}
        <Box
          sx={{
            p: 3,
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Icon Container */}
          <Box
            component="img"
            src="/assets/images/icons/menu.png"
            alt="Menu Item Icon"
            sx={{
              width: 48,
              height: 48,
              minWidth: 48,
              objectFit: "contain",
              filter:
                "brightness(0) saturate(100%) invert(43%) sepia(99%) saturate(1235%) hue-rotate(244deg)",
              opacity: 0.8,
            }}
          />

          {/* Title and Subtitle */}
          <Box sx={{ flexGrow: 1, pr: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 0.5,
                letterSpacing: 0.2,
              }}
            >
              Menu Item Details
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.875rem",
              }}
            >
              View menu item information
            </Typography>
          </Box>

          {/* Close Button */}
          <IconButton
            onClick={() => setViewModal({ open: false, item: null })}
            sx={{
              mt: -0.5,
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: `${theme.palette.primary.main}20`,
                color: theme.palette.primary.main,
              },
              transition: "all 0.2s ease",
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* ===== Content ===== */}
        <Box
          sx={{
            p: 3,
            flexGrow: 1,
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: 6,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.background.default,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.divider,
              borderRadius: 3,
              "&:hover": {
                backgroundColor: theme.palette.text.disabled,
              },
            },
          }}
        >
          {viewModal.item && (
            <Stack spacing={3}>
              {/* Menu Item Image */}
              {viewModal.item.image && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mb: 1,
                  }}
                >
                  <ImageLoader
                    imagePath={viewModal.item.image}
                    width={150}
                    height={150}
                    alt={viewModal.item.name}
                    rounded={true}
                    borderRadius={2}
                  />
                </Box>
              )}

              {/* Menu Item Name */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {viewModal.item.name}
                </Typography>
              </Box>

              {/* Category */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Category
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {viewModal.item.Category.name}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Sub Category
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {viewModal.item.SubCategory.name}
                </Typography>
              </Box>

              {/* Base Price */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Base Price
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  LKR {(Number(viewModal.item.basePrice) || 0).toFixed(2)}
                </Typography>
              </Box>

              {/* Description */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Description
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.6,
                  }}
                >
                  {viewModal.item.description}
                </Typography>
              </Box>

              {/* Preparation Time */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Preparation Time
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {viewModal.item.preparationTime} minutes
                </Typography>
              </Box>

              {/* Dietary Info */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Dietary Information
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  {viewModal.item.isVegetarian && (
                    <Chip
                      label="Vegetarian"
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                  {viewModal.item.isVegan && (
                    <Chip
                      label="Vegan"
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                  {viewModal.item.isSpicy && (
                    <Chip
                      label="Spicy"
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  )}
                  {viewModal.item.hasVariations && (
                    <Chip
                      label="Has Variations"
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>

              {/* Status */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Status
                </Typography>
                <Chip
                  label={viewModal.item.isActive ? "Active" : "Inactive"}
                  variant="filled"
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    letterSpacing: 0.3,
                    background: viewModal.item.isActive
                      ? `linear-gradient(135deg, ${theme.palette.success.main}15 0%, ${theme.palette.success.light}15 100%)`
                      : `linear-gradient(135deg, ${theme.palette.warning.main}15 0%, ${theme.palette.warning.light}15 100%)`,
                    color: viewModal.item.isActive
                      ? theme.palette.success.dark
                      : theme.palette.warning.dark,
                    border: `2px solid ${
                      viewModal.item.isActive
                        ? theme.palette.success.main
                        : theme.palette.warning.main
                    }`,
                    borderRadius: "20px",
                    padding: "4px 8px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: viewModal.item.isActive
                        ? `0 4px 12px ${theme.palette.success.main}40`
                        : `0 4px 12px ${theme.palette.warning.main}40`,
                    },
                  }}
                />
              </Box>
            </Stack>
          )}
        </Box>
      </Dialog>
    </DataSetupMainCard>
  );
};

export default MenuItemsSetupCard;
